import React from 'react';
import classNames from 'classnames';

import { applyExternalShrineBuff, applyExternalSkillBuff, selectStats } from 'logic/stats';

import Data from 'data';
import Locale from 'data/locale';
import Store from 'store';

import SkillPanel, { displayUnitNames, SkillIcon } from './Skill';
import MonsterSection, { UnitItems } from './Monster';
import EditingContext from 'store/editingContext';
import Accordion from 'ui/accordion';
import { unitIcon } from 'components/stats/Stats';
import TooltipBuilder from 'tooltips/builder';
import { Color } from 'common';
import Tooltip from 'ui/tooltip';
import { itemGetName, statsToLines } from 'logic/item';
import { allSkillsInfo } from 'logic/skills/skillinfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { D2ItemTooltip, D2SkillTooltip } from 'tooltips';
import Select from 'ui/select';
import { itemNameColored } from 'components/equipment/EquipmentPane';
import NumberInput from 'ui/number';
import shrineBuffs from 'logic/skills/shrines';
import AdUnit from 'AdUnit';

const _L = Locale("ui-calculator");

function BuffTooltip({ id, level, stats, sourceName }) {
  const skill = Data.skills[id];
  const desc = Data.skillDesc[skill?.skilldesc];
  const name = Locale.strings[shrineBuffs[id]?.name || desc?.strname] || skill?.skill;
  const builder = new TooltipBuilder(<>
    {name}
    <span className={`d2-color-${Color.GRAY}`}> {'\u2013'} {_L`Level ${level}`}</span>
  </>);
  if (sourceName) {
    builder.body.push(<div className={`d2-color-${Color.GRAY}`}>{_L`Applied by ${sourceName}`}</div>);
  }
  for (const line of statsToLines({...stats})) {
    builder.body.push(<div className={`d2-color-${Color.BLUE}`}>{line[1]}</div>);
  }
  builder.body.push(<div className={`d2-color-${Color.RED}`}>{_L`Click to remove`}</div>);
  return builder.build();
}

function BuffEntry({ state, skill: id, level, stats, sourceName, onRemove }) {
  const skill = Data.skills[id];
  const desc = Data.skillDesc[skill?.skilldesc];
  const name = Locale.strings[shrineBuffs[id]?.name || desc?.strname] || skill?.skill;
  const onClick = React.useCallback(() => onRemove(state), [state, onRemove]);
  return (
    <Tooltip tooltip={<BuffTooltip id={id} level={level} stats={stats} sourceName={sourceName}/>}>
      <span className="d2p-buff-entry" onClick={onClick}>
        {!!skill && <SkillIcon id={id} size={20}/>}
        {!!shrineBuffs[id] && <span className={`d2p-ShrineIcon d2p-${id}`}/>}
        {' '}{name}
      </span>
    </Tooltip>
  );
}

function HiddenSkills({ unit, skills }) {
  const dispatch = Store.useDispatch();
  const onClick = key => dispatch.profile.setActive(key, active => ({...active, hidden: false}));
  return (
    <div className="d2p-unit-hidden-skills">
      <FontAwesomeIcon icon={faEye} className="d2p-button"/>
      {skills.map(({ key, id }) => (
        <D2SkillTooltip key={key} id={id} unit={unit} full={false}>
          <SkillIcon id={id} color={Color.GRAY} onClick={() => onClick(key)}/>
        </D2SkillTooltip>
      ))}
    </div>
  );
}

const renderItem = ({ id, item }, search) => <D2ItemTooltip id={id}>{itemNameColored(item, search)}</D2ItemTooltip>;

function IronGolemItem() {
  const items = Store.useSelector(store => store.items);
  const options = React.useMemo(() => Object.entries(items).filter(([id, item]) => {
    const base = Data.items[item?.base];
    return base && (base.bitfield1 & 2);
  }).map(([id, item]) => ({id, name: itemGetName(item), item})), [items]);
  const currentId = Store.useProfile(profile => profile.ironGolem);
  const value = options.find(opt => opt.id === currentId);

  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(value => dispatch.setProfile(profile => ({...profile, ironGolem: value.id})), [dispatch]);

  return (
    <div>
      <Select options={options} value={value} onChange={onChange} renderOption={renderItem} renderValue={renderItem} placholder={_L`Select item`}/>
    </div>
  );
}

function SummonItems({ summon }) {
  const dispatch = Store.useDispatch();

  const onChange = React.useCallback(update => dispatch.setSummons(summons => {
    const prev = summons[summon.monster]?.items;
    if (!prev) return summons;
    const items = update(prev);
    if (items === prev) return prev;
    summons = {...summons};
    summons[summon.monster] = {...summons[summon.monster], items};
    return summons;
  }), [dispatch, summon.monster]);

  return (
    <div className="d2p-MonsterSection">
      <UnitItems id={summon.monster} level={summon.itemLevel} unitLevel={summon.skillLevel} items={summon.items} onChange={onChange}/>
    </div>
  );
}

function getBuffOptions() {
  const options = [
    {title: _L`Defensive Auras`, options: []},
    {title: _L`Personal Buffs`, options: []},
    {title: _L`Warcries`, options: []},
    {title: _L`Offensive Auras`, options: []},
    {title: _L`Curses`, options: []},
    {title: _L`Shrines`, options: []},
  ];
  for (const [id, skill] of Object.entries(Data.skills)) {
    if (skill.srvdofunc === 18 && !skill.charclass) continue;
    if (skill.srvdofunc !== 18 && !skill.auratargetstate) continue;
    const name = Locale.strings[Data.skillDesc[skill.skilldesc]?.strname] || skill.skill;
    switch (skill.srvdofunc) {
    case 30:
      options[4].options.push({id, name});
      break;
    case 65:
      options[0].options.push({id, name});
      break;
    case 66:
    case 81:
      options[3].options.push({id, name});
      break;
    case 68:
      options[2].options.push({id, name});
      break;
    case 18:
      options[1].options.push({id, name});
      break;
    // no default
    }
  }
  for (const [id, shrine] of Object.entries(shrineBuffs)) {
    if (!shrine.stats || shrine.hidden) continue;
    options[5].options.push({id, name: Locale.strings[shrine.name], shrine: true});
  }
  return options;
}

function ExternalBuffSection() {
  const [buff, setBuff] = React.useState();
  const [level, setLevel] = React.useState(20);

  const store = Store.useStore();
  const onAdd = React.useCallback(team => {
    if (!buff) return;
    const stats = selectStats(store.getState());
    if (buff.shrine) {
      store.dispatch.setBuffs(buffs => applyExternalShrineBuff(buffs, stats, buff.id));
    } else {
      store.dispatch.setBuffs(buffs => applyExternalSkillBuff(buffs, stats, buff.id, level, team));
    }
  }, [store, buff, level]);

  return (
    <div className="d2p-Section d2p-ExternalBuffs d2p-expanded d2p-always">
      <h3>{_L`Add External Buffs`}</h3>
      <div className="d2p-content">
        <Select options={getBuffOptions}
                value={buff}
                onChange={setBuff}
                placeholder={_L`Select Buff`}
                />
        {!buff?.shrine && <label>{_L`Level`} <NumberInput min={1} max={99} value={level} onChange={setLevel}/></label>}
        <button onClick={() => onAdd(0)}>{_L`Add Friendly`}</button>
        {!buff?.shrine && <button onClick={() => onAdd(1)}>{_L`Add Hostile`}</button>}
      </div>
    </div>
  );
}

function UnitSection({ unit, stats, target, skills }) {
  const [expanded, setExpanded] = EditingContext.useState("calculatorSection-" + unit, true);
  const active = Store.useProfile(profile => profile.active);
  const summon = Store.useSelector(state => state.summons[stats.class]);

  const buffs = Store.useSelector(state => state.buffs?.[unit])?.filter(buff => {
    if (buff.filter && (!(buff.filter & stats.aura_include) || (buff.filter & stats.aura_exclude))) return false;
    return true;
  });

  const dispatch = Store.useDispatch();
  const onRemove = React.useCallback(state => dispatch.setBuffs(buffs => {
    buffs = {...buffs};
    buffs[unit] = buffs[unit]?.filter(buff => buff.state !== state);
    return buffs;
  }), [dispatch, unit]);

  const onUnsummon = React.useCallback(e => {
    e.stopPropagation();
    dispatch.setSummons(summons => {
      summons = {...summons};
      if (stats.revive) {
        delete summons[stats.unit];
      } else {
        delete summons[stats.class];
      }
      return summons;
    });
  }, [dispatch, stats]);

  if (!skills.length && unit !== 'monster' && !buffs?.length) return null;

  const hidden = skills.filter(sk => active[sk.key]?.hidden);

  return <>
    {unit === 'monster' && (
      <AdUnit id="d2planner-tab-calculations-1" className="d2planner-tab-ad d2planner-tab-ad-middle"/>
    )}
    <div className={classNames("d2p-Section d2p-unit-section", {"d2p-expanded": expanded})}>
      <h3 className="d2p-unit-header" onClick={() => setExpanded(e => !e)}>
        <span className={`d2p-class-icon d2p-icon-large d2p-class-${unitIcon(unit, stats.class)}`}/>
        {displayUnitNames[unit] ? _L(displayUnitNames[unit]) : stats.name}
        {(stats.type === 'summon' || stats.revive) && (
          <D2SkillTooltip id="3" level={1} canLevel="special"><SkillIcon id="3" size={32} onClick={onUnsummon}/></D2SkillTooltip>
        )}
      </h3>
      <Accordion visible={expanded} className="d2p-unit-data">
        {!!(buffs?.length || hidden.length || stats.class === 'irongolem') && (
          <div className="d2p-unit-extras">
            {!!hidden.length && <HiddenSkills unit={unit} skills={hidden}/>}
            {!!buffs?.length && (
              <div className="d2p-unit-buffs">
                <span className="d2p-buff-label">{_L`Buffs/Debuffs:`}</span>
                {buffs.map(buff => <BuffEntry key={buff.state} {...buff} onRemove={onRemove}/>)}
              </div>
            )}
            {stats.class === 'irongolem' && <IronGolemItem/>}
          </div>
        )}
        {unit === 'monster' && <MonsterSection/>}
        {!!(stats.type === 'summon' && summon.items) && <SummonItems summon={summon}/>}
        {skills.map(({ key, id, ...props }) => !active[key]?.hidden && <SkillPanel key={key} activeKey={key} unit={unit} stats={stats} id={id} target={target} {...props}/>)}
      </Accordion>
    </div>
  </>;
}

export default React.memo(function CalculatorPane() {
  const stats = Store.useSelector(selectStats);
  const info = React.useMemo(() => allSkillsInfo(stats), [stats]);

  return (
    <div className="d2p-CalculatorPane">
      {Object.entries(stats).map(([unit, unitstats]) => <UnitSection key={unit} unit={unit} stats={unitstats} target={info[unit]?.target} skills={info[unit]?.skills}/>)}
      <ExternalBuffSection/>
    </div>
  );
});
